import { ExternalLink, ShoppingBag, Truck, ShieldCheck, Info, CalendarHeart, ArrowRight, Sparkles } from "lucide-react";

const products = [
  {
    id: "aftercare-clearshield",
    name: "Aftercare for ClearShield Glass",
    desc: "The recommended weekly maintenance spray to keep ClearShield‑treated glass performing at its best.",
    price: "£9.00",
    badge: "Most popular",
    url: "https://www.wholesaleglasscompany.co.uk/shop/ritec-aftercare-for-clearshield-glass.html",
  },
  {
    id: "aftercare-shower",
    name: "Aftercare for Shower Glass",
    desc: "Specially formulated for treated shower screens — protects the coating and lifts away soap & water marks.",
    price: "£11.00",
    url: "https://www.wholesaleglasscompany.co.uk/shop/ritec-aftercare-for-shower-glass.html",
  },
  {
    id: "limescale-remover",
    name: "Ritec Limescale Remover",
    desc: "500ml trigger spray. Powerful but glass‑safe limescale remover from Ritec — kind to your coating.",
    price: "£10.25",
    url: "https://www.wholesaleglasscompany.co.uk/shop/limescale-remover.html",
  },
  {
    id: "shower-care-kit",
    name: "Ritec Shower Care Kit",
    desc: "Everything you need to maintain a treated shower in one box — aftercare spray, limescale remover & cloth.",
    price: "£19.50",
    badge: "Complete kit",
    url: "https://www.wholesaleglasscompany.co.uk/shop/ritec-shower-care-kit.html",
  },
  {
    id: "shower-twin-pack",
    name: "Shower Care Twin Pack",
    desc: "Twin pack of Ritec Aftercare & Limescale Remover — great value for households with multiple bathrooms.",
    price: "£18.23",
    url: "https://www.wholesaleglasscompany.co.uk/shop/ritec-shower-care-twin-pack.html",
  },
  {
    id: "microfibre-cloth",
    name: "Microfibre e‑cloth",
    desc: "The right cloth makes all the difference. Use with the aftercare sprays for streak‑free results.",
    price: "£3.75",
    url: "https://www.wholesaleglasscompany.co.uk/shop/microfibre-e-cloth.html",
  },
];

export default function Aftercare() {
  return (
    <section
      id="aftercare"
      data-testid="aftercare-section"
      className="relative py-24 md:py-32 px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-emerald-700">
              Aftercare shop
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">
              Keep your glass <span className="italic font-semibold">flawless</span>{" "}
              for years.
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              We recommend Ritec’s official aftercare range — supplied by our
              trusted partner, Wholesale Glass Co. Order direct, delivered
              straight to your door.
            </p>
          </div>
          <div className="flex flex-col gap-2 md:items-end">
            <div className="inline-flex items-center gap-2 text-sm text-emerald-800 font-semibold bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-full">
              <Truck size={14} /> UK next‑working‑day delivery on most orders
            </div>
          </div>
        </div>

        {/* Warranty notice */}
        <div
          data-testid="warranty-notice"
          className="mb-10 rounded-3xl overflow-hidden bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-950 text-white p-7 md:p-10 shadow-xl shadow-emerald-950/20"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-emerald-200">
                <ShieldCheck size={14} />
                Your 10‑year warranty
              </div>
              <h3 className="mt-3 font-display text-2xl md:text-3xl font-semibold leading-tight">
                Just had your glass treated? Here’s how to keep your warranty valid.
              </h3>
              <p className="mt-4 text-emerald-50/90 leading-relaxed max-w-xl">
                Your ClearShield coating is guaranteed for 10 years —
                <strong className="text-white"> provided </strong>
                it is maintained using the approved Ritec aftercare products
                shown below. Using abrasive cleaners, scouring pads or
                harsh chemicals will damage the coating and may invalidate
                your warranty.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-emerald-50/90">
                <li className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-300 flex-shrink-0" />
                  Clean weekly with <strong className="text-white">Ritec Aftercare Spray</strong> and a soft microfibre cloth.
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-300 flex-shrink-0" />
                  Use <strong className="text-white">Ritec Limescale Remover</strong> for hard‑water stains — never standard descaler.
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-300 flex-shrink-0" />
                  Avoid abrasive sponges, bleach, vinegar or any acidic / alkaline cleaners.
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-300 flex-shrink-0" />
                  Keep your invoice as proof of treatment — we’ll be on hand if you ever need us.
                </li>
              </ul>
            </div>

            <div className="md:col-span-4">
              <div className="rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-6 text-center">
                <ShieldCheck size={32} className="mx-auto text-emerald-200" />
                <div className="mt-3 font-display text-5xl font-bold leading-none">10</div>
                <div className="text-sm font-semibold tracking-wide mt-2">Year warranty</div>
                <div className="text-xs text-emerald-100/80 mt-3 leading-relaxed">
                  Stay protected by sticking to the approved aftercare range below.
                </div>
              </div>
            </div>
          </div>

          <div className="mt-7 pt-6 border-t border-white/10 flex items-start gap-3 text-xs text-emerald-100/80">
            <Info size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              Not sure which product you need? Drop us a WhatsApp or call —
              we’re happy to recommend the right kit for your treated glass.
            </span>
          </div>
        </div>

        <div className="mb-6 flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-emerald-700">
          <ShieldCheck size={14} />
          Approved aftercare · Required to maintain your 10‑year warranty
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map((p) => (
            <a
              key={p.id}
              data-testid={`aftercare-card-${p.id}`}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative rounded-3xl bg-white border border-slate-200 p-6 hover-lift overflow-hidden flex flex-col"
            >
              {p.badge && (
                <div className="absolute top-4 right-4 text-[10px] font-bold tracking-widest uppercase bg-emerald-950 text-emerald-100 px-3 py-1 rounded-full">
                  {p.badge}
                </div>
              )}

              <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <ShoppingBag size={20} />
              </div>

              <div className="mt-5 flex-1">
                <div className="text-[10px] font-bold tracking-widest uppercase text-slate-500">
                  Ritec · ClearShield range
                </div>
                <div className="mt-1 font-display text-xl font-semibold text-emerald-950 leading-tight">
                  {p.name}
                </div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  {p.desc}
                </p>
              </div>

              <div className="mt-6 pt-5 border-t border-slate-100 flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    From
                  </div>
                  <div className="font-display text-2xl font-bold text-emerald-950 leading-none mt-1">
                    {p.price}
                  </div>
                </div>
                <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 group-hover:text-emerald-900 transition-colors">
                  Buy now
                  <ExternalLink
                    size={14}
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </div>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a
            data-testid="aftercare-shop-all"
            href="https://www.wholesaleglasscompany.co.uk/shop/shop-by-brand/ritec-brand-page.html"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-950 text-white font-semibold hover:bg-emerald-700 transition-colors"
          >
            Shop the full Ritec range
            <ExternalLink size={16} />
          </a>
          <div className="mt-3 text-xs text-slate-500">
            You’ll be taken to Wholesale Glass Co. to complete your order.
          </div>
        </div>

        {/* Maintenance package CTA */}
        <div
          data-testid="maintenance-package-cta"
          className="mt-16 relative rounded-3xl overflow-hidden bg-white border border-emerald-200 shadow-xl shadow-emerald-900/10"
        >
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-emerald-100/60 blur-3xl" />
          <div className="absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-emerald-200/40 blur-3xl" />

          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 p-8 md:p-12 items-center">
            <div className="md:col-span-8">
              <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-emerald-700">
                <CalendarHeart size={14} />
                Prefer us to do it for you?
              </div>
              <h3 className="mt-3 font-display text-3xl md:text-4xl font-semibold leading-tight text-emerald-950">
                Hands‑off maintenance — let our team keep your glass perfect.
              </h3>
              <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
                We offer flexible maintenance packages so you don’t have to lift a finger.
                Choose how often you’d like us to visit — once a year, twice, three times,
                or just whenever you need us. We’ll handle the cleaning, the limescale,
                and a professional health‑check of your ClearShield coating, keeping
                your warranty in perfect standing.
              </p>

              <div className="mt-6 flex flex-wrap gap-2">
                {["Annual", "Twice a year", "Quarterly", "Ad‑hoc visits"].map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full"
                  >
                    <Sparkles size={12} /> {tag}
                  </span>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-4">
                <a
                  data-testid="maintenance-inquiry-btn"
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    const messageEl = document.querySelector('[data-testid="contact-input-message"]');
                    if (messageEl) {
                      const setter = Object.getOwnPropertyDescriptor(
                        window.HTMLTextAreaElement.prototype,
                        "value"
                      ).set;
                      setter.call(
                        messageEl,
                        "Hi J7 Collective — I’d like to enquire about a maintenance package for my treated glass. Please let me know what options you offer (frequency, pricing, what’s included)."
                      );
                      messageEl.dispatchEvent(new Event("input", { bubbles: true }));
                    }
                    document
                      .getElementById("contact")
                      ?.scrollIntoView({ behavior: "smooth" });
                    setTimeout(() => messageEl?.focus(), 600);
                  }}
                  className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-950 text-white font-semibold hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-950/20"
                >
                  Send a maintenance enquiry
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </a>
                <a
                  href="https://wa.me/447497498669?text=Hi%20J7%20Collective%20%E2%80%94%20I%E2%80%99d%20like%20to%20enquire%20about%20a%20maintenance%20package%20for%20my%20treated%20glass."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="maintenance-whatsapp-btn"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-emerald-200 text-emerald-800 font-semibold hover:bg-emerald-50 transition-colors"
                >
                  Or ask on WhatsApp
                </a>
              </div>
            </div>

            <div className="md:col-span-4">
              <div className="rounded-2xl bg-gradient-to-br from-emerald-700 to-emerald-950 p-6 text-white text-center">
                <CalendarHeart size={28} className="mx-auto text-emerald-200" />
                <div className="mt-4 font-display text-2xl font-bold leading-tight">
                  Set & forget
                </div>
                <div className="mt-2 text-sm text-emerald-100/90 leading-relaxed">
                  We diary you in, turn up on the day, and leave your glass like new — every time.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
